import { QueryConfig } from 'redux-query';
import { getSortQueryParam } from '../../helpers/getSortQueryParam';
import { IAssignmentDto } from '../../interfaces/onlineTraining/AssignmentDto';
import { IAssignmentStatsDto } from '../../interfaces/onlineTraining/AssignmentStatsDto';
import { IMyTrainingFilter } from '../../interfaces/onlineTraining/MyTrainingFilter';
import { IPagination } from '../../interfaces/pagination/Pagination';
import { IPaginatedResult } from '../../interfaces/queries/paginatedResult';
import { ISort } from '../../interfaces/Sort';
import { IModuleFilter } from '../../interfaces/onlineTraining/ModuleFilter';
import { IModuleDto } from '../../interfaces/onlineTraining/ModuleDto';
import { IAssignmentCompletionDto } from '../../interfaces/onlineTraining/AssignmentCompletionDto';
import produce from 'immer';
import { IAssignmentCompletionResponseDto } from '../../interfaces/onlineTraining/AssignmentCompletionResponseDto';
import { ICategoryDto } from '../../interfaces/onlineTraining/CategoryDto';
import { IAssignmentFormDto } from '../../interfaces/onlineTraining/AssignmentFormDto';
import { ITrainingSummaryBrandDto } from '../../interfaces/onlineTraining/TrainingSummaryDto';
import { ITrainingSummaryFilter } from '../../interfaces/onlineTraining/TrainingSummaryFilter';
import { useMutationWithAuth } from '../../hooks/useMutationWithAuth';
import { IListFilesItemProps } from 'lqa-ui/lib/Components/ListFilesItem';

interface IWithMyTraining {
    assignments: IPaginatedResult<IAssignmentDto>;
    assignmentStats: IAssignmentStatsDto;
    selectedModule: IModuleDto;
    categories: ICategoryDto[];
    summary: ITrainingSummaryBrandDto[];
    hotelSummary: IPaginatedResult<IAssignmentDto>;
    tutorialVideo: {
        id: number,
        name: string,
        pictureUrl: string,
        videoUrl: string,
    }[];
}

interface IWithAssignmentEntities {
    entities: IWithMyTraining;
}

export interface IWithBrandsAttachments {
    upfrontAttachments?: IListFilesItemProps[];
}

export interface IWithBrandAttchmentsEntities {
    entities: IWithBrandsAttachments;
}

export const getAssignmentsQuery = (pagination: IPagination, sort: ISort, filter: IMyTrainingFilter): QueryConfig<IWithMyTraining> => ({
    url: `${Env.API_URL}/onlineTraining/assignments`,
    transform: response => ({ assignments: response }),
    update: {
        assignments: (oldValue, newValue) => newValue,
    },
    body: {
        ...pagination,
        sort: getSortQueryParam(sort, 'name'),
        ...filter,
    },
});

export const getAssignmentStatsQuery = (filter: IMyTrainingFilter): QueryConfig<IWithMyTraining> => ({
    url: `${Env.API_URL}/onlineTraining/assignmentStats`,
    transform: response => ({ assignmentStats: response }),
    update: {
        assignmentStats: (oldValue, newValue) => newValue,
    },
    body: {
        ...filter,
    },
});

export const getModuleQuery = (filter: IModuleFilter): QueryConfig<IWithMyTraining> => ({
    url: `${Env.API_URL}/onlineTraining/module`,
    transform: response => ({ selectedModule: response }),
    update: {
        selectedModule: (oldValue, newValue) => newValue,
    },
    body: {
        ...filter,
    },
});

export const getAllModulesQuery = (filter: IMyTrainingFilter): QueryConfig<IWithMyTraining> => ({
    url: `${Env.API_URL}/onlineTraining/allModules`,
    transform: response => ({ categories: response }),
    update: {
        categories: (oldValue, newValue) => newValue,
    },
    body: {
        ...filter,
    },
});

export const getTutorialVideos = (): QueryConfig<IWithMyTraining> => ({
    url: `${Env.API_URL}/onlineTraining/tutorialVideos`,
    transform: response => ({ tutorialVideo: response }),
    update: {
        tutorialVideo: (oldValue, newValue) => newValue,
    },
});

export const getTrainingSummaryQuery = (filter: ITrainingSummaryFilter): QueryConfig<IWithMyTraining> => {
    return {
        url: `${Env.API_URL}/onlineTraining/trainingSummary`,
        transform: response => ({ summary: response }),
        update: {
            summary: (oldValue, newValue) => newValue,
        },
        body: {
            ...filter,
        },
    };
};

export const getHotelTrainingSummaryQuery = (sort: ISort, pagination?: IPagination, filter?: ITrainingSummaryFilter)
    : QueryConfig<IWithMyTraining> => {
    return {
        url: `${Env.API_URL}/onlineTraining/hotelTrainingSummary`,
        transform: response => ({ hotelSummary: response }),
        update: {
            hotelSummary: (oldValue, newValue) => newValue,
        },
        body: {
            ...pagination,
            sort: getSortQueryParam(sort, 'name'),
            ...filter,
        },
    };
};

export const useSubmitAssignment = () => useMutationWithAuth<IWithMyTraining>((completion: IAssignmentCompletionDto) => {
    return {
        url: `${Env.API_URL}/onlineTraining/completeModule`,
        options: {
            method: 'POST',
        },
        body: completion,
        transform: body => ({ assignments: body, categories: body, assignmentStats: body }),
        update: {
            assignments: produce((prev?: IPaginatedResult<IAssignmentDto>, next?: IAssignmentCompletionResponseDto) => {
                if (next && prev) {
                    if (next.isBestScore) {
                        prev.results.forEach((result) => {
                            if (result.assignmentId === next.assignmentId) {
                                result.bestScore = next.score;
                                if (next.assignmentPassed) {
                                    result.dateCompleted = next.completionDate;
                                }
                            }
                        });
                    }
                }
            }),
            categories: produce((prev?: ICategoryDto[], next?: IAssignmentCompletionResponseDto) => {
                if (next && prev) {
                    if (next.isBestScore) {
                        prev.forEach((category) => {
                            const moduleToUpdate = category.modules.find((module) => {
                                return module.id === next.moduleId;
                            });
                            if (moduleToUpdate) {
                                moduleToUpdate.bestScore = next.score;
                            }
                        });
                    }
                }
            }),
            assignmentStats: (oldValue, newValue) => newValue,
        },
    };
});

export const useAssignModulesToUser = () => useMutationWithAuth<IWithMyTraining>((assignmentForm: IAssignmentFormDto) => {
    return {
        url: `${Env.API_URL}/onlineTraining/assignModules`,
        options: {
            method: 'POST',
        },
        body: assignmentForm,
    };
});

export const useGetCorrectAnswerId = () => useMutationWithAuth<IWithMyTraining>((questionId: number) => {
    return {
        url: `${Env.API_URL}/onlineTraining/getCorrectAnswerId`,
        options: {
            method: 'POST',
        },
        body: { questionId },
    };
});

export const onlineTrainingSelector = (state: IWithAssignmentEntities) => state.entities;

export const useDeleteOnlineTraining = () => useMutationWithAuth<IWithMyTraining>((id: number) => ({
    url: `${Env.API_URL}/onlineTraining/${id}`,
    options: {
        method: 'DELETE',
    },
}));

export const getUpfrontAttachmentQuery = (body: {
    brandId?: number,
    hotelId?: number,
}): QueryConfig<IWithBrandsAttachments> => ({
    url: `${Env.API_URL}/upfrontTraining/getAttachments`,
    body,
    transform: data => ({ upfrontAttachments: data }),
    update: {
        upfrontAttachments: (oldValue, newValue) => newValue,
    },
});

export const upfrontAttachmentsSelector = (state: IWithBrandAttchmentsEntities) => state.entities.upfrontAttachments;
